import {
  ChangeEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  Input,
  Form,
  DatePicker,
  Select,
  ConfigProvider,
} from 'antd';
import dayjs from 'dayjs';
import { CloseCircleTwoTone, SearchOutlined } from '@ant-design/icons';
import Card from 'components/layout/Card';
import Title from 'components/layout/Title';
import PatientList from 'components/patients/PatientList';
import Pagination from 'components/ui/pagination/Pagination';
import type { Dayjs } from 'dayjs';
import useDebounce from 'hooks/useDebounce';
import { useNavigate } from 'react-router-dom';
import { PatientStatus, PATIENT_STATUSES, REQUEST_STATUSES } from 'stores/common';
import { usePatientList } from 'stores/patients/patientsHooks';

const DATE_FORMAT = 'DD/MM/YYYY';

export default function Patients() {
  const [searchForm] = Form.useForm();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<PatientStatus | null>(null);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs().startOf('day'),
    null,
  ]);
  const [sortOrder, setSortOrder] = useState<'ascend' | null>('ascend');
  const [currentPagePreviousPatients, setCurrentPagePreviousPatients] = useState(1);
  const [previousPatients, previousPatientsRequestStatus] = usePatientList({
    page: currentPagePreviousPatients,
    name: search,
    status,
    startDate: dateRange[0],
    endDate: dateRange[1],
    sortOrder,
  });

  const onClickPatient = useCallback(
    (patientID: string) => navigate(`/patients/${patientID}`),
    [navigate],
  );
  const onChangeSearch = useDebounce(
    (event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value),
  );
  const onClearSearch = useCallback(
    () => {
      searchForm.resetFields(['search']);
      setSearch('');
    },
    [setSearch, searchForm],
  );
  const onChangeDateRange = useCallback(
    (newDateRange: null | [Dayjs | null, Dayjs | null]) => {
      if (newDateRange) {
        setDateRange(newDateRange);
      } else {
        setDateRange([null, null]);
      }
    },
    [setDateRange],
  );
  const onChangeStatus = useCallback((newStatus: PatientStatus) => setStatus(newStatus), []);
  const onClearStatus = useCallback(() => setStatus(null), []);
  const statusOptions = useMemo(
    () => Object.values(PATIENT_STATUSES).map((value) => ({ value, label: value })),
    [],
  );
  const onChangeOrder = useCallback(
    (newSortOrder: 'ascend' | null) => {
      setSortOrder(newSortOrder);
    },
    [],
  );

  return (
    <div>
      <div className='flex flex-wrap space-x-4'>
        <Title main>Mes patients</Title>
        <div className='mb-4 w-[200px]'>
          <Form form={searchForm}>
            <Form.Item name='search'>
              <Input
                onChange={onChangeSearch}
                prefix={<SearchOutlined />}
                placeholder='Rechercher…'
                disabled={!!status}
                suffix={search ? (
                  <CloseCircleTwoTone
                    twoToneColor='#94a3b8'
                    onClick={onClearSearch}
                    className='opacity-75 hover:opacity-100'
                  />
                ) : null}
              />
            </Form.Item>
          </Form>
        </div>
        <div className='mb-4'>
          <DatePicker.RangePicker
            defaultValue={dateRange}
            onChange={onChangeDateRange}
            format={DATE_FORMAT}
          />
        </div>
        <div className='mb-4'>
          <ConfigProvider
            theme={{
              token: {
                fontFamily: 'Montserrat',
                colorPrimary: '#fc6a38',
                colorTextBase: '#1E2248',
              },
              components: {
                Select: {
                  borderRadius: 6,
                },
              },
            }}
          >
            <Select
              placeholder='Filtrer par statut'
              onChange={onChangeStatus}
              onClear={onClearStatus}
              options={statusOptions}
              disabled={search !== ''}
              className='!rounded-full w-52'
              allowClear
            />
          </ConfigProvider>
        </div>
      </div>
      <Card>
        <PatientList
          loading={previousPatientsRequestStatus === REQUEST_STATUSES.PENDING}
          onClickPatient={onClickPatient}
          patients={previousPatients}
          onChangeOrder={onChangeOrder}
          sortOrder={sortOrder}
        />
        <div className='flex justify-end pt-4'>
          <Pagination
            loading={previousPatientsRequestStatus === REQUEST_STATUSES.PENDING}
            onChangePage={setCurrentPagePreviousPatients}
            currentPage={currentPagePreviousPatients}
            // TODO: handle last page correctly
            isOnLastPage={previousPatients?.length ? previousPatients?.length < 10 : false}
          />
        </div>
      </Card>
    </div>
  );
}
